import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAdminTenant } from '@/hooks/useAdminTenants'
import TenantOverviewCard from '@/components/admin/TenantOverviewCard'
import TenantUsersCard from '@/components/admin/TenantUsersCard'
import TenantFeaturesCard from '@/components/admin/TenantFeaturesCard'
import TenantAttestationsCard from '@/components/admin/TenantAttestationsCard'
import TenantActivityCard from '@/components/admin/TenantActivityCard'

type TenantTab = 'overview' | 'users' | 'features' | 'attestations' | 'activity'

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<TenantTab>('overview')

  const { data: tenant, isLoading, error } = useAdminTenant(id || null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading tenant...</div>
      </div>
    )
  }

  if (error || !tenant) {
    return (
      <div className="card p-6 bg-red-50 border-red-200">
        <p className="text-red-700">Failed to load tenant details.</p>
      </div>
    )
  }

  const TABS = [
    { key: 'overview' as const, label: 'Overview' },
    { key: 'users' as const, label: 'Users' },
    { key: 'features' as const, label: 'Features' },
    { key: 'attestations' as const, label: 'Attestations' },
    { key: 'activity' as const, label: 'Activity' },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500">
        <Link to="/employee/admin" className="hover:text-personic-600">
          Admin Panel
        </Link>
        {' / '}
        <Link to="/employee/admin" className="hover:text-personic-600">
          Tenants
        </Link>
        {' / '}
        <span className="text-gray-900">{tenant.name}</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
        <p className="text-gray-600 mt-1">{tenant.slug}</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-personic-600 text-personic-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <TenantOverviewCard tenant={tenant} />}
        {activeTab === 'users' && <TenantUsersCard tenantId={id!} />}
        {activeTab === 'features' && <TenantFeaturesCard tenantId={id!} tenantName={tenant.name} />}
        {activeTab === 'attestations' && <TenantAttestationsCard tenantId={id!} />}
        {activeTab === 'activity' && <TenantActivityCard tenantId={id!} />}
      </div>
    </div>
  )
}
